import * as Yup from 'yup';
import moment from 'moment';
import vitality from '../constants/enums/vitality';
import deathInstant from '../constants/enums/deathInstant';
import { breastfeedingDischargeOptions } from '../constants/options';
import sectionAFields from '../assets/sectionAFields.json';
import sectionBFields from '../assets/sectionBFields.json';
import childBirthSchema from '../components/forms/sectionB/child-birth/form/schema';

export const motherMeasurementLimits = {
  motherHeight: { min: 110, max: 210, label: 'Altezza madre', unit: 'cm' },
  motherWeightAtChildbirth: { min: 40, max: 250, label: 'Peso madre al parto', unit: 'kg' },
  motherWeightBeforePregnancy: { min: 40, max: 200, label: 'Peso pregravidico', unit: 'kg' },
};

const isEmpty = (value) => value == null || (typeof value === 'string' && value.trim() === '');

const isTrue = (value) => value === true || value === 'true';

const isFalse = (value) => value === false || value === 'false';

const isRecord = (value) => value !== null && typeof value === 'object' && !Array.isArray(value);

const isRecordArray = (value) => Array.isArray(value) && value.every(isRecord);

const isSameChild = (first, second) => {
  if (first === second) return true;
  if (!isEmpty(first.childId) && !isEmpty(second.childId))
    return String(first.childId) === String(second.childId);
  if (!isEmpty(first._id) && !isEmpty(second._id)) return String(first._id) === String(second._id);
  return !isEmpty(first.sdo) && !isEmpty(second.sdo) && String(first.sdo) === String(second.sdo);
};

const requiredWhenFilled = (otherField) =>
  Yup.string().test(
    `required-with-${otherField}`,
    'Campo obbligatorio',
    function requiredWithOther(value) {
      return isEmpty(this.parent[otherField]) || !isEmpty(value);
    }
  );

export const previousChildbirthDateSchema = (childbirthDate) =>
  Yup.date()
    .transform((value) => {
      if (typeof value === 'string') {
        const parsedDate = moment(value);
        return parsedDate.isValid() ? parsedDate.toDate() : null;
      }
      return value;
    })
    .test(
      'previous-childbirth-date',
      "La data dell'ultimo parto non può essere successiva alla data del parto corrente",
      (value) => !value || !childbirthDate || !moment(value).isAfter(moment(childbirthDate), 'day')
    )
    .test(
      'previous-childbirth-date-minimum',
      'La data del parto corrente deve essere ad almeno 7 mesi dalla data dell\u2019ultimo parto',
      (value) =>
        !value ||
        !childbirthDate ||
        moment(childbirthDate).isSameOrAfter(moment(value).add(7, 'months'), 'day')
    )
    .nullable();

export const genitalMutilationSchema = () =>
  Yup.string()
    .transform((value) => (isEmpty(value) ? undefined : value))
    .oneOf(
      [...Object.keys(sectionBFields.mutilations.options), 'not_available'],
      'Valore non valido'
    )
    .required('Campo obbligatorio');

export const breastfeedingDischargeSchema = () =>
  Yup.string()
    .transform((value) => (isEmpty(value) ? undefined : value))
    .nullable()
    .oneOf(Object.keys(breastfeedingDischargeOptions), 'Valore non valido')
    .when('params.vitality', {
      is: vitality.BORN_ALIVE,
      then: (schema) => schema.required('Campo obbligatorio'),
      otherwise: (schema) => schema.optional(),
    });

export const malformation1Schema = () =>
  Yup.string().when('malformation1Notes', {
    is: (value) => !isEmpty(value),
    then: (schema) => schema.required('Campo obbligatorio'),
  });

export const revivedTechniquesSchema = () =>
  Yup.array()
    .nullable()
    .transform((value) => (value == null ? [] : value))
    .when('revived', {
      is: (value) => value === true || value === 'true',
      then: (schema) => schema.required('Campo obbligatorio').min(1, 'Campo obbligatorio'),
    });

export const sectionAControlsSchema = Yup.object().shape({
  parentsInfo: Yup.object().shape({
    mother: Yup.object().shape({
      maritalStatus: Yup.string()
        .transform((value) => (isEmpty(value) ? undefined : value))
        .oneOf(
          Object.keys(sectionAFields.parentsInfo.mother.maritalStatus.options),
          'Valore non valido'
        )
        .required('Campo obbligatorio'),
    }),
  }),
});

const optionalNumber = () =>
  Yup.number()
    .transform((value, originalValue) => (isEmpty(originalValue) ? null : value))
    .nullable()
    .typeError('Inserire un valore numerico')
    .test('finite', 'Inserire un valore numerico', (value) =>
      value == null ? true : Number.isFinite(value)
    );

const booleanField = () =>
  Yup.boolean()
    .transform((value) => (value === '' ? null : value))
    .nullable();

const requiredBooleanField = () => booleanField().required('Campo obbligatorio');

export const motherMeasurementSchema = (name) => {
  const { min, max, unit } = motherMeasurementLimits[name];
  const message = `Inserire un valore compreso tra ${min} e ${max} ${unit}`;
  return optionalNumber().min(min, message).max(max, message);
};

export const malformationNumberSchema = (childMalformation, { min, max }) => {
  const schema = optionalNumber().min(min).max(max);
  return isTrue(childMalformation) ? schema.required('Campo obbligatorio') : schema;
};

export const malformationControlFields = (childMalformation) => ({
  malformation1: malformation1Schema(),
  malformation1Notes: requiredWhenFilled('malformation1'),
  malformation2: Yup.string().test(
    'different-malformation',
    'Le malformazioni devono essere diverse',
    function differentMalformation(value) {
      return !value || value !== this.parent.malformation1;
    }
  ),
  malformation3: Yup.string().test(
    'different-malformation',
    'Le malformazioni devono essere diverse',
    function differentMalformation(value) {
      return !value || ![this.parent.malformation1, this.parent.malformation2].includes(value);
    }
  ),
  gestationalAge: malformationNumberSchema(childMalformation, { min: 0, max: 43 }),
  age: malformationNumberSchema(childMalformation, { min: 0, max: 11 }),
});

const pregnancyCheckLabels = {
  checkups: 'Visite di controllo in gravidanza',
  echographies: 'Numero ecografie refertate',
};

export const pregnancyCheckCountSchema = () => {
  const message = 'Inserire un valore compreso tra 0 e 44 oppure 99 (Non noto)';
  return optionalNumber()
    .integer('Inserire un numero intero')
    .test(
      'range-or-unknown',
      message,
      (value) => value == null || (value >= 0 && value <= 44) || value === 99
    )
    .required('Campo obbligatorio');
};

const pregnancyChecksSchema = Yup.object().shape({
  checkups: pregnancyCheckCountSchema(),
  echographies: pregnancyCheckCountSchema(),
});

export const resolveDeathCondition = (sectionD) => {
  if (!isEmpty(sectionD?.disease)) return sectionD.disease;
  if (!isEmpty(sectionD?.fetusDisease) || !isEmpty(sectionD?.fetusDiseaseNotes)) return 'known';

  const hasCompiledData =
    sectionD &&
    Object.entries(sectionD).some(
      ([key, value]) =>
        !['referentValidationD', 'rectifiedD', 'referentMalformation'].includes(key) &&
        !isEmpty(value)
    );

  return hasCompiledData ? 'unknown' : '';
};

// Per il decesso dopo la nascita si usa convenzionalmente il valore 3 del tracciato 2026.
export const getPostnatalDeathInstant = (childVitality) =>
  [vitality.DECEASED, vitality.BORN_ALIVE_DECEASED_WITHIN_WEEK].includes(childVitality)
    ? deathInstant.DEATH_IN_LABOR_OR_CHILDBIRTH
    : null;

export const deathInstantSchema = (childVitality) => {
  const fixedValue = getPostnatalDeathInstant(childVitality);
  return Yup.string()
    .transform((value) => (isEmpty(value) ? undefined : value))
    .typeError('Valore non valido')
    .oneOf(fixedValue ? [fixedValue] : Object.values(deathInstant), 'Valore non valido')
    .required('Campo obbligatorio');
};

export const deathControlFields = (childVitality) => {
  const required = [vitality.STILLBORN, vitality.DECEASED].includes(childVitality);
  const mortalitySection = [
    vitality.STILLBORN,
    vitality.DECEASED,
    vitality.BORN_ALIVE_DECEASED_WITHIN_WEEK,
  ].includes(childVitality);
  const disease = Yup.string()
    .transform((value) => (isEmpty(value) ? null : value))
    .nullable()
    .oneOf(['known', 'unknown', null], 'Valore non valido');
  const week = optionalNumber();

  return {
    disease: mortalitySection ? disease.required('Campo obbligatorio') : disease,
    fetusDisease: Yup.string()
      .nullable()
      .when('disease', {
        is: 'known',
        then: (schema) => schema.trim().required('Campo obbligatorio'),
      }),
    fetusDiseaseNotes: Yup.string()
      .nullable()
      .when('disease', {
        is: 'known',
        then: (schema) =>
          schema
            .trim()
            .min(5, 'La descrizione deve essere almeno di 5 caratteri')
            .max(300)
            .required('Campo obbligatorio'),
      }),
    gestationalWeekAtDeath: required ? week.required('Campo obbligatorio') : week,
    instrumentalExamsPerformed: required ? requiredBooleanField() : booleanField(),
    photographs: (required ? requiredBooleanField() : booleanField())
      .test(
        'instrumental-exams-required',
        'Se gli esami strumentali sono eseguiti, selezionare almeno uno tra Fotografie ed Esame della placenta',
        function instrumentalExamsRequired(value) {
          if (!isTrue(this.parent.instrumentalExamsPerformed)) return true;
          return isTrue(value) || isTrue(this.parent.placentaExamRequested);
        }
      )
      .test(
        'instrumental-exams-not-performed',
        'Fotografie ed Esame della placenta non possono essere Sì se gli esami strumentali non sono eseguiti',
        function instrumentalExamsNotPerformed(value) {
          if (!isFalse(this.parent.instrumentalExamsPerformed)) return true;
          return !isTrue(value) && !isTrue(this.parent.placentaExamRequested);
        }
      ),
    autopsyPerformed: required ? requiredBooleanField() : booleanField(),
    placentaExamRequested: required ? requiredBooleanField() : booleanField(),
    ...([vitality.STILLBORN, vitality.DECEASED, vitality.BORN_ALIVE_DECEASED_WITHIN_WEEK].includes(
      childVitality
    ) && { deathInstant: deathInstantSchema(childVitality) }),
  };
};

const motherMeasurementsSchema = Yup.object().shape(
  Object.keys(motherMeasurementLimits).reduce(
    (fields, name) => ({ ...fields, [name]: motherMeasurementSchema(name) }),
    {}
  )
);

const collectErrors = (schema, values) => {
  try {
    schema.validateSync(values, { abortEarly: false });
    return [];
  } catch (error) {
    if (!(error instanceof Yup.ValidationError)) throw error;
    return error.inner;
  }
};

const birthFieldLabels = {
  place: 'Luogo del parto',
  laborModality: 'Inizio del travaglio',
  inductionTypes: 'Metodo utilizzato per l’induzione',
  inductionReason: 'Motivo di induzione',
  acceleration: 'Accelerazione',
  laborManagement: 'Conduzione del travaglio',
  freePositionsDuringLabor: 'Posizioni libere in travaglio',
  analgesiaDetailsArray: 'Controllo del dolore',
  preLaborCesareanReason: 'Motivo TC pre-travaglio',
  childbirthKind: 'Genere del parto',
  males: 'N° nati maschi',
  females: 'N° nati femmine',
  attendantHCP: 'Personale sanitario presente al parto',
  presenceBirthRoom: 'Presenza in sala parto',
  newbornVitality: 'Dati neonati',
  genderValidation: 'Conteggio sesso neonati',
};

const birthNewbornFieldLabels = {
  gender: 'Sesso',
  vitalityCheckbox: 'Nato Morto',
  vitality: 'Vitalità',
  malformation: 'Presenza di malformazione',
  childbirthModality: 'Modalità parto',
  childbirthOrder: 'Ordine di nascita',
  presentation: 'Presentazione fetale',
  kristellerManeuver: 'Uso di manovra di Kristeller',
  vacuumExtraction: 'Uso di ventosa',
  forceps: 'Uso di forcipe',
  anesthesia: 'Anestesia',
  episiotomy: 'Episiotomia',
  severeLacerations: 'Presenza di lacerazioni di 3° o 4° grado',
  inLaborCesareanReason: 'Motivo del TC in travaglio',
};

const humanizeFieldName = (field) =>
  field.replace(/([A-Z])/g, ' $1').replace(/^./, (value) => value.toUpperCase());

const getBirthControlLabel = (path = '') => {
  const relativePath = path.replace(/^childbirthData\./, '');
  const newbornMatch = relativePath.match(/^newbornVitality\[(\d+)\]\.(.+)$/);

  if (newbornMatch) {
    const index = Number(newbornMatch[1]);
    const field = newbornMatch[2];
    return `Neonato ${index + 1} — ${birthNewbornFieldLabels[field] ?? humanizeFieldName(field)}`;
  }

  return birthFieldLabels[relativePath] ?? humanizeFieldName(relativePath);
};

const deathControlLabels = {
  disease: 'Condizione del decesso',
  fetusDisease: 'Codice ICD9',
  fetusDiseaseNotes: 'Descrizione del decesso',
  gestationalWeekAtDeath: 'Settimana gestazionale del decesso',
  deathInstant: 'Momento del decesso',
  instrumentalExamsPerformed: 'Esami strumentali',
  photographs: 'Fotografie',
  autopsyPerformed: 'Riscontro autoptico',
  placentaExamRequested: 'Esame della placenta',
};

const childSectionControlLabels = {
  breastfeedingDischarge: 'Allattamento durante la degenza',
  revivedTechniques: 'Tecnica di rianimazione',
  malformation1: 'Malformazioni congenite alla dimissione I diagnosi - Codice ICD9',
  malformation1Notes: 'Malformazioni congenite alla dimissione I diagnosi - Descrizione',
  malformation2: 'Malformazioni congenite alla dimissione II diagnosi - Codice ICD9',
  malformation3: 'Malformazioni congenite alla dimissione III diagnosi - Codice ICD9',
  gestationalAge: 'Età gestazionale alla diagnosi',
  age: 'Età neonatale alla diagnosi',
};

export const collectCedapControlErrors = (
  cedap,
  {
    children = cedap.children || [],
    siblings = children,
    includeMother = true,
    includeChildSections = true,
    groups,
  } = {}
) => {
  const checksGroup = (group) => !groups || groups.includes(group);
  let childrenGroup = includeChildSections ? 'newborn' : 'mortalityNeonatologist';
  if (checksGroup('mortalityStillborn')) childrenGroup = 'mortalityStillborn';
  if (!isRecordArray(children))
    return [
      {
        path: 'children',
        message: 'Dati dei neonati non validi. Ricaricare la scheda o correggere i dati salvati.',
        childId: null,
        group: childrenGroup,
      },
    ];
  if (includeChildSections && checksGroup('newborn') && !isRecordArray(siblings))
    return [
      {
        path: 'children',
        message: 'Impossibile controllare l’ordine di nascita: elenco dei neonati non valido.',
        childId: null,
        group: 'newborn',
      },
    ];

  const errors = includeMother
    ? collectErrors(motherMeasurementsSchema, cedap.sectionB?.pregnancyData || {}).map((error) => ({
        path: `sectionB.pregnancyData.${error.path}`,
        message: `${motherMeasurementLimits[error.path]?.label ?? 'Dati antropometrici madre'}: ${
          error.message
        }`,
        childId: null,
        group: 'mother',
      }))
    : [];

  if (includeMother) {
    collectErrors(sectionAControlsSchema, cedap.sectionA || {}).forEach((error) => {
      errors.push({
        path: `sectionA.${error.path}`,
        message: `Stato civile madre: ${error.message}`,
        childId: null,
        group: 'mother',
      });
    });

    collectErrors(pregnancyChecksSchema, cedap.sectionB?.pregnancyData?.checks || {}).forEach(
      (error) => {
        errors.push({
          path: `sectionB.pregnancyData.checks.${error.path}`,
          message: `${pregnancyCheckLabels[error.path]}: ${error.message}`,
          childId: null,
          group: 'mother',
        });
      }
    );

    collectErrors(
      Yup.object().shape({ genitalMutilation: genitalMutilationSchema() }),
      cedap.sectionB?.pregnancyData || {}
    ).forEach((error) => {
      errors.push({
        path: `sectionB.pregnancyData.${error.path}`,
        message: `Presenza di mutilazione dei genitali: ${error.message}`,
        childId: null,
        group: 'mother',
      });
    });

    const newMotherFieldLabels = {
      folicAcid: 'Assunzione acido folico',
      assistedConceptionDetails:
        'Concepimento con tecniche di Procreazione Medicalmente Assistita (PMA)',
    };

    collectErrors(
      Yup.object().shape({
        folicAcid: Yup.string()
          .transform((value) => (value == null || value === '' ? undefined : value))
          .required('Campo obbligatorio'),
        assistedConceptionDetails: Yup.string()
          .transform((value) => (value == null || value === '' ? undefined : value))
          .required('Campo obbligatorio'),
      }),
      cedap.sectionB?.pregnancyData || {}
    ).forEach((error) => {
      errors.push({
        path: `sectionB.pregnancyData.${error.path}`,
        message: `${newMotherFieldLabels[error.path]}: ${error.message}`,
        childId: null,
        group: 'mother',
      });
    });

    const previousConceptions = cedap.sectionB?.pregnancyData?.previousConceptions;
    if (previousConceptions === true || previousConceptions === 'true') {
      collectErrors(
        Yup.object().shape({
          previousConceptionsData: Yup.object().shape({
            previousChildbirthDate: previousChildbirthDateSchema(cedap.childbirthDate),
          }),
        }),
        cedap.sectionB?.pregnancyData || {}
      ).forEach((error) => {
        errors.push({
          path: `sectionB.pregnancyData.${error.path}`,
          message: `Data ultimo parto precedente: ${error.message}`,
          childId: null,
          group: 'mother',
        });
      });
    }
  }

  if (groups?.includes('birth')) {
    const childbirthData = cedap.sectionB?.childbirthData || {};
    const { attendantHCP } = childbirthData;
    // The API stores checkbox selections as an object; the form schema uses an array.
    const birthValues = {
      childbirthData: {
        ...childbirthData,
        attendantHCP: Array.isArray(attendantHCP)
          ? attendantHCP
          : Object.keys(attendantHCP || {}).filter((key) => isTrue(attendantHCP[key])),
      },
    };

    collectErrors(childBirthSchema, birthValues).forEach((error) => {
      errors.push({
        path: error.path,
        message: `${getBirthControlLabel(error.path)}: ${error.message}`,
        childId: null,
        group: 'birth',
      });
    });
  }

  const newborns = cedap.sectionB?.childbirthData?.newbornVitality;
  const birthNewborns = isRecordArray(newborns) ? newborns : [];
  const matchesNewborn = (child, newborn) => {
    if (!isEmpty(child.childId) && !isEmpty(newborn._id))
      return String(child.childId) === String(newborn._id);
    return (
      !isEmpty(child.sectionC?.childbirthOrder) &&
      !isEmpty(newborn.childbirthOrder) &&
      String(child.sectionC.childbirthOrder) === String(newborn.childbirthOrder)
    );
  };

  if (checksGroup('mortalityStillborn')) {
    birthNewborns.forEach((newborn, index) => {
      if (newborn.vitality !== vitality.STILLBORN) return;
      if (!children.some((child) => matchesNewborn(child, newborn)))
        errors.push({
          path: `sectionB.childbirthData.newbornVitality[${index}]`,
          message: `Neonato ${
            newborn.childbirthOrder || index + 1
          } — Compilare le sezioni E e F del nato morto prima di procedere.`,
          childId: newborn._id || null,
          group: 'mortalityStillborn',
        });
    });
  }

  children.forEach((child, index) => {
    const childVitality = child.sectionC?.params?.vitality;
    const birthNewborn = birthNewborns.find((newborn) => matchesNewborn(child, newborn));
    const mortalityGroup =
      childVitality === vitality.STILLBORN || birthNewborn?.vitality === vitality.STILLBORN
        ? 'mortalityStillborn'
        : 'mortalityNeonatologist';
    const checksChildSections = includeChildSections && checksGroup('newborn');
    const checksMortality = checksGroup(mortalityGroup);
    if (!checksChildSections && !checksMortality) return;

    const childId = child.childId || child._id || child.sdo;
    const childbirthOrder = child.sectionC?.childbirthOrder;
    const order = childbirthOrder || index + 1;
    const label = `Neonato ${order}${childId ? ` (${childId})` : ''}`;

    if (!Object.values(vitality).includes(childVitality)) {
      errors.push({
        path: `children[${index}].sectionC.params.vitality`,
        message: `${label} — Vitalità assente o non valida: correggere i dati del neonato prima di procedere.`,
        childId,
        group: checksChildSections ? 'newborn' : mortalityGroup,
      });
      return;
    }

    if (
      birthNewborn &&
      (birthNewborn.vitality === vitality.STILLBORN) !== (childVitality === vitality.STILLBORN)
    ) {
      errors.push({
        path: `children[${index}].sectionC.params.vitality`,
        message: `${label} — La vitalità non è coerente con i dati del Parto.`,
        childId,
        group: checksChildSections ? 'newborn' : mortalityGroup,
      });
      return;
    }

    if (checksChildSections && !isEmpty(childbirthOrder)) {
      // Siblings provide comparison context; only the selected children are validated.
      const hasDuplicateOrder = siblings.some(
        (sibling) =>
          !isSameChild(child, sibling) &&
          !isEmpty(sibling.sectionC?.childbirthOrder) &&
          String(sibling.sectionC.childbirthOrder) === String(childbirthOrder)
      );
      if (hasDuplicateOrder) {
        errors.push({
          path: `children[${index}].sectionC.childbirthOrder`,
          message: `${label} — Ordine del nato nel presente parto: già inserito`,
          childId,
          group: 'newborn',
        });
      }
    }

    if (checksMortality) {
      const sectionD = child.sectionD || {};
      collectErrors(Yup.object().shape(deathControlFields(childVitality)), {
        ...sectionD,
        disease: resolveDeathCondition(sectionD),
      }).forEach((error) => {
        errors.push({
          path: `children[${index}].sectionD.${error.path}`,
          message: `${label} — ${deathControlLabels[error.path]}: ${error.message}`,
          childId,
          group: mortalityGroup,
        });
      });
    }

    if (!checksChildSections) return;

    const sectionC = child.sectionC || {};
    collectErrors(
      Yup.object().shape({
        breastfeedingDischarge: breastfeedingDischargeSchema(),
        revivedTechniques: revivedTechniquesSchema(),
      }),
      sectionC
    ).forEach((error) => {
      errors.push({
        path: `children[${index}].sectionC.${error.path}`,
        message: `${label} — ${childSectionControlLabels[error.path]}: ${error.message}`,
        childId,
        group: 'newborn',
      });
    });

    const sectionE = child.sectionE || {};
    collectErrors(
      Yup.object().shape(malformationControlFields(child.sectionC?.malformation)),
      sectionE
    ).forEach((error) => {
      errors.push({
        path: `children[${index}].sectionE.${error.path}`,
        message: `${label} — ${childSectionControlLabels[error.path]}: ${error.message}`,
        childId,
        group: 'newborn',
      });
    });
  });

  if (groups) return errors.filter(({ group }) => groups.includes(group));

  return errors;
};

const joinControlMessages = (errors) => errors.map(({ message }) => message).join('; ');

const buildMissingFieldsMessage = (errors) => {
  const missingFields = errors.filter(({ message }) => message.endsWith('Campo obbligatorio'));
  const otherErrors = errors.filter(({ message }) => !message.endsWith('Campo obbligatorio'));

  if (!missingFields.length) return joinControlMessages(errors);

  const fields = missingFields
    .map(({ message }) => `- ${message.replace(/: Campo obbligatorio$/, '')}`)
    .join('\n');

  let message = `Dalla scheda risultano non compilati i seguenti campi:\n${fields}\nI campi sono obbligatori come da specifica del Ministero della Salute`;

  if (otherErrors.length) {
    message += `\n\nAltri controlli non superati:\n${otherErrors
      .map(({ message: otherMessage }) => `- ${otherMessage}`)
      .join('\n')}`;
  }

  return message;
};

export const assertCedapControls = (cedap, options = {}) => {
  const errors = collectCedapControlErrors(cedap, options);
  if (errors.length) {
    const message = options.missingFieldsMessage
      ? buildMissingFieldsMessage(errors)
      : joinControlMessages(errors);
    const error = new Error(message);
    error.validationErrors = errors;
    throw error;
  }
};
